<template>
    <div class="box">
        <h2 class="title is-4">New .293</h2>
        
        <!-- Aircraft Tail Number -->
        <div class="columns is-multiline">
            <div class="column is-full">
                <SearchDropdown
                    label="Aircraft Tail Number"
                    placeholder="Search for tail number"
                    :loading="tailNumbersLoading"
                    loadingText="Loading tail numbers..."
                    :items="aircrafts"
                    v-model="selectedAircraft"
                    v-model:searchText="tailNumberSearch"
                    :validationError="aircraftValidationError"
                    :touched="aircraftTouched"
                    noResultsText="No matching tail numbers found"
                    :searchFields="['value']"
                    displayField="value"
                    @select="selectAircraft"
                    @blur="handleAircraftBlur"
                />
            </div>
        </div>

        <!-- Pilot Name Section - Only show when aircraft is selected -->
        <div class="columns is-multiline" v-if="isAircraftValid">
            <div class="column is-full">
                <SearchDropdown
                    label="Pilot Name"
                    placeholder="Search for pilot"
                    :loading="pilotsLoading"
                    loadingText="Loading pilots..."
                    :items="availablePilots"
                    v-model="selectedPilot"
                    v-model:searchText="pilotSearch"
                    :validationError="pilotValidationError"
                    :touched="pilotTouched"
                    noResultsText="No matching pilots found"
                    :searchFields="['firstName', 'lastName']"
                    displayField="fullName"
                    @select="selectPilot"
                    @blur="handlePilotBlur"
                >
                    <template #item="{ item }">
                        {{ item.firstName }} {{ item.lastName }}
                    </template>
                </SearchDropdown>
            </div>

            <!-- Check Airman Section (Only shown if pilot is valid) -->
            <div class="column is-full" v-if="isPilotValid">
                <SearchDropdown
                    label="Check Airman"
                    placeholder="Search for check airman"
                    :loading="checkAirmansLoading"
                    loadingText="Loading check airmen..."
                    :items="checkAirmans"
                    v-model="selectedCheckAirman"
                    v-model:searchText="airmanSearch"
                    :validationError="airmanValidationError"
                    :touched="airmanTouched"
                    noResultsText="No matching check airmen found"
                    :searchFields="['firstName', 'lastName']"
                    displayField="fullName"
                    @select="selectAirman"
                    @blur="handleAirmanBlur"
                >
                    <template #item="{ item }">
                        {{ item.firstName }} {{ item.lastName }}
                    </template>
                </SearchDropdown>
            </div>

            <!-- Date/Time Section (Only shown if check airman is valid) -->
            <div class="column is-full" v-if="isPilotValid && isCheckAirmanValid">
                <div class="field">
                    <label class="label">Scheduled Date & Time</label>
                    <div class="control">
                        <input 
                            class="input"
                            type="datetime-local"
                            v-model="scheduledDateTime"
                            :class="{ 'is-danger': dateTimeValidationError && dateTimeTouched }"
                            @blur="handleDateTimeBlur"
                        />
                    </div>
                    <div v-if="dateTimeValidationError && dateTimeTouched" class="notification is-danger is-light">
                        {{ dateTimeValidationError }}
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Submit Button - Only enabled when all validations pass -->
        <div class="field is-grouped mt-4">
            <div class="control is-expanded">
                <button class="button is-info is-fullwidth" @click="submitCheckride" 
                        :disabled="!isFormValid || isSubmitting"
                        :class="{ 'is-loading': isSubmitting }">
                    {{ isSubmitting ? 'Creating Checkride...' : 'Submit' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import SearchDropdown from '@/components/SearchDropdown.vue';
import { useCheckrides } from '@/lib/checkrides';
import { functions, appwriteConfig } from '@/lib/appwrite';
import { useCheckridesStore } from '@/store/checkridesStore';

export default {
    name: 'New293Form',
    components: {
        SearchDropdown
    },
    emits: ['success', 'error'],
    setup(props, { emit }) {
        const selectedAircraft = ref('');
        const aircrafts = ref([]);
        const tailNumbersLoading = ref(false);
        const tailNumberSearch = ref('');
        const aircraftValidationError = ref('');
        const aircraftTouched = ref(false);

        const selectedPilot = ref('');
        const pilotSearch = ref('');
        const pilotValidationError = ref('');
        const pilotTouched = ref(false);
        const availablePilots = ref([]);
        const pilotsLoading = ref(false);
        
        const selectedCheckAirman = ref('');
        const checkAirmans = ref([]);
        const checkAirmansLoading = ref(false);
        const airmanValidationError = ref('');
        const airmanSearch = ref('');
        const airmanTouched = ref(false);

        const scheduledDateTime = ref('');
        const dateTimeValidationError = ref('');
        const dateTimeTouched = ref(false);

        const isSubmitting = ref(false);
        
        const checkrides = useCheckrides();
        const checkridesStore = useCheckridesStore();

        const validateAircraft = () => {
            if (!selectedAircraft.value) {
                aircraftValidationError.value = "Please select an aircraft tail number";
                return false;
            }
            aircraftValidationError.value = '';
            return true;
        };

        // Validation functions
        const validatePilot = () => {
            if (!selectedPilot.value) {
                pilotValidationError.value = "Please select a pilot";
                return false;
            }
            pilotValidationError.value = '';
            return true;
        };

        const validateCheckAirman = () => {
            if (!selectedCheckAirman.value) {
                airmanValidationError.value = "Please select a check airman";
                return false;
            }
            airmanValidationError.value = '';
            return true;
        };

        const validateDateTime = () => {
            if (!scheduledDateTime.value) {
                dateTimeValidationError.value = "Please select a date and time";
                return false;
            }
            
            const selectedDate = new Date(scheduledDateTime.value);
            const now = new Date();
            
            if (selectedDate <= now) {
                dateTimeValidationError.value = "Please select a future date and time";
                return false;
            }
            
            dateTimeValidationError.value = '';
            return true;
        };

        // Computed validation states
        const isAircraftValid = computed(() => {
            if (!aircraftTouched.value && !selectedAircraft.value) return false;
            validateAircraft();
            return selectedAircraft.value && !aircraftValidationError.value;
        });

        const isPilotValid = computed(() => {
            if (!pilotTouched.value && !selectedPilot.value) return false;
            validatePilot();
            return selectedPilot.value && !pilotValidationError.value;
        });
        
        const isCheckAirmanValid = computed(() => {
            if (!isPilotValid.value) return false;
            if (!airmanTouched.value && !selectedCheckAirman.value) return false;
            validateCheckAirman();
            return selectedCheckAirman.value && !airmanValidationError.value;
        });
        
        const isDateTimeValid = computed(() => {
            if (!isCheckAirmanValid.value) return false;
            if (!dateTimeTouched.value && !scheduledDateTime.value) return false;
            validateDateTime();
            return scheduledDateTime.value && !dateTimeValidationError.value;
        });
        
        // Overall form validity
        const isFormValid = computed(() => {
            return isAircraftValid.value && isPilotValid.value && isCheckAirmanValid.value && isDateTimeValid.value;
        });

        // Event handlers
        const selectPilot = (pilot) => {
            selectedPilot.value = pilot.id;
            pilotSearch.value = `${pilot.firstName} ${pilot.lastName}`;
            pilotTouched.value = true;
            validatePilot();
        };

        const selectAirman = (airman) => {
            selectedCheckAirman.value = airman.id;
            airmanSearch.value = `${airman.firstName} ${airman.lastName}`;
            airmanTouched.value = true;
            validateCheckAirman();
        };

        const selectAircraft = () => {
            aircraftTouched.value = true;
            validateAircraft();
        };

        const handleAircraftBlur = () => {
            if (tailNumberSearch.value) {
                aircraftTouched.value = true;
                validateAircraft();
            }
        };

        const handlePilotBlur = () => {
            if (pilotSearch.value) {
                pilotTouched.value = true;
                validatePilot();
            }
        };

        const handleAirmanBlur = () => {
            if (airmanSearch.value) {
                airmanTouched.value = true;
                validateCheckAirman();
            }
        };

        const handleDateTimeBlur = () => {
            if (scheduledDateTime.value) {
                dateTimeTouched.value = true;
                validateDateTime();
            }
        };

        // Data loading functions
        const loadTailNumbers = async () => {
            tailNumbersLoading.value = true;
            try {
                const tailNumbers = await checkridesStore.fetchTailNumbers();
                aircrafts.value = Object.entries(tailNumbers).map(([id, value]) => ({ id, value }));
            } catch (e) {
                console.error('Error fetching tail numbers', e);
                emit('error', 'Failed to load aircraft data. Please try again.');
            } finally {
                tailNumbersLoading.value = false;
            }
        };
        const loadPilots = async () => {
            pilotsLoading.value = true;
            try {
                const pilotData = await checkrides.fetchPilots();
                
                availablePilots.value = Object.entries(pilotData).map(([id, pilot]) => ({
                    id,
                    firstName: pilot.firstName,
                    lastName: pilot.lastName,
                    fullName: `${pilot.firstName} ${pilot.lastName}`
                }));
            } catch (error) {
                console.error('Error fetching pilots:', error);
                emit('error', "Failed to load pilots. Please try again.");
                availablePilots.value = [];
            } finally {
                pilotsLoading.value = false;
            }
        };

        const loadCheckAirmans = async () => {  
            checkAirmansLoading.value = true;
            try {
                const airmenData = await checkrides.fetchCheckAirmen();
                
                checkAirmans.value = Object.entries(airmenData).map(([id, airman]) => ({
                    id,
                    firstName: airman.firstName,
                    lastName: airman.lastName,
                    fullName: `${airman.firstName} ${airman.lastName}`
                }));
            } catch (error) {
                console.error('Error fetching check airmen:', error);
                emit('error', "Failed to load check airmen. Please try again.");
                checkAirmans.value = [];
            } finally {
                checkAirmansLoading.value = false;
            }
        };

        // Watch for aircraft selection to reset pilot and subsequent fields
        watch(selectedAircraft, (newAircraft, oldAircraft) => {
            // If aircraft is deselected, reset all subsequent fields
            if (!newAircraft && oldAircraft) {
                selectedPilot.value = '';
                pilotSearch.value = '';
                pilotValidationError.value = '';
                pilotTouched.value = false;
                
                selectedCheckAirman.value = '';
                airmanSearch.value = '';
                airmanValidationError.value = '';
                airmanTouched.value = false;
                
                scheduledDateTime.value = '';
                dateTimeValidationError.value = '';
                dateTimeTouched.value = false;
            }
        });

        // Watch for pilot selection to load check airmen
        watch(selectedPilot, () => {
            selectedCheckAirman.value = '';
            airmanSearch.value = '';
            airmanValidationError.value = '';
            airmanTouched.value = false;
            
            if (isPilotValid.value) {
                loadCheckAirmans();
            }
        });

        // Submit function
        const submitCheckride = async () => {
            // Mark all sections as touched when attempting to submit
            pilotTouched.value = true;
            airmanTouched.value = true;
            dateTimeTouched.value = true;
            
            // Validate all sections before submitting
            if (!validatePilot() || !validateCheckAirman() || !validateDateTime()) {
                return;
            }

            // Convert local datetime to Zulu (UTC)
            const localDateTime = new Date(scheduledDateTime.value);
            const zuluDateTime = localDateTime.toISOString();

            const data = {
                pilotId: selectedPilot.value,
                checkAirmanId: selectedCheckAirman.value,
                scheduledDateTime: zuluDateTime,
                type: "293",
                aircraftId: selectedAircraft.value
            };

            try {
                isSubmitting.value = true;
                const response = await functions.createExecution(
                    appwriteConfig.functions.createCheckride,
                    JSON.stringify(data)
                );
                const result = JSON.parse(response.responseBody);
                
                if (result.success) {
                    // Reset form
                    selectedAircraft.value = '';
                    tailNumberSearch.value = '';
                    aircraftValidationError.value = '';
                    aircraftTouched.value = false;
                    
                    selectedPilot.value = '';
                    pilotSearch.value = '';
                    selectedCheckAirman.value = '';
                    airmanSearch.value = '';
                    scheduledDateTime.value = '';
                    pilotValidationError.value = '';
                    airmanValidationError.value = '';
                    dateTimeValidationError.value = '';
                    
                    // Reset touched states
                    pilotTouched.value = false;
                    airmanTouched.value = false;
                    dateTimeTouched.value = false;
                    
                    emit('success', result.checkride);
                } else {
                    emit('error', `Failed to create checkride: ${result.error}`);
                }
            } catch (error) {
                console.error('Error submitting checkride:', error);
                emit('error', `Error submitting checkride: ${error.message}`);
            } finally {
                isSubmitting.value = false;
            }
        };

        // Initialize on mount
        onMounted(async () => {
            checkrides.initialize();
            await loadTailNumbers();
            await loadPilots();
        });

        return {
            selectedAircraft,
            aircrafts,
            tailNumbersLoading,
            tailNumberSearch,
            aircraftValidationError,
            aircraftTouched,
            selectedPilot,
            pilotSearch,
            pilotValidationError,
            pilotTouched,
            availablePilots,
            pilotsLoading,
            selectedCheckAirman,
            checkAirmans,
            checkAirmansLoading,
            airmanValidationError,
            airmanSearch,
            airmanTouched,
            scheduledDateTime,
            dateTimeValidationError,
            dateTimeTouched,
            isSubmitting,
            isAircraftValid,
            isPilotValid,
            isCheckAirmanValid,
            isDateTimeValid,
            isFormValid,
            selectAircraft,
            selectPilot,
            selectAirman,
            handleAircraftBlur,
            handlePilotBlur,
            handleAirmanBlur,
            handleDateTimeBlur,
            submitCheckride
        };
    }
};
</script>